import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

import { Avatar, avatarTitle } from '../utils/avatar'
import { csrfToken } from '../utils/csrfToken'

function markdownHtml(content) {
  return { __html: DOMPurify.sanitize(marked.parse(content || '')) }
}

function Reactions({ learning, onToggle }) {
  return (
    <div className="d-flex gap-2 mt-3">
      {(learning.reactions || []).map((reaction) => (
        <button
          key={reaction.emoji}
          type="button"
          className={`btn btn-sm ${reaction.reacted ? 'btn-primary' : 'btn-white'}`}
          onClick={() => onToggle(learning.id, reaction.emoji)}
        >
          {reaction.emoji} {reaction.count > 0 ? reaction.count : ''}
        </button>
      ))}
    </div>
  )
}

function LearningCard({ learning, onToggle }) {
  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-auto">
            <Avatar user={learning.user} />
          </div>
          <div className="col ms-n2">
            <h4 className="mb-1">{avatarTitle(learning.user)}</h4>
            <p className="card-text small text-body-secondary mb-0">
              {learning.applicable_date_label}
            </p>
          </div>
        </div>
        <div className="py-3" dangerouslySetInnerHTML={markdownHtml(learning.content)} />
        <Reactions learning={learning} onToggle={onToggle} />
      </div>
    </div>
  )
}

function SharedLearnings({ url }) {
  const [learnings, setLearnings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const payload = await response.json()
        if (!cancelled) setLearnings(payload.learnings || [])
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [url])

  async function toggleReaction(id, emoji) {
    const response = await fetch(`/shared_learnings/${id}/toggle_reaction`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken() || '',
      },
      body: JSON.stringify({ emoji }),
      credentials: 'same-origin',
    })
    if (!response.ok) return

    const payload = await response.json()
    setLearnings((current) =>
      current.map((learning) =>
        learning.id === id ? { ...learning, reactions: payload.reactions } : learning
      )
    )
  }

  if (loading) return <div className="text-body-secondary">Loading shared learnings…</div>
  if (error) return <div className="alert alert-danger">{error}</div>
  if (!learnings.length) {
    return (
      <div className="card">
        <div className="card-body text-body-secondary">
          No shared learnings yet. Submit a daily report and check "Share publicly" to see it here.
        </div>
      </div>
    )
  }

  return (
    <div>
      {learnings.map((learning) => (
        <LearningCard key={learning.id} learning={learning} onToggle={toggleReaction} />
      ))}
    </div>
  )
}

export function mountSharedLearnings(el) {
  const url = el?.dataset?.url
  if (!url) return
  const root = createRoot(el)
  root.render(<SharedLearnings url={url} />)
}
